/**
 * Centralized Reservation Utilities
 *
 * This utility provides a single source of truth for all reservation-related
 * business logic, handling both vehicle-level (nextReserveStatus) and
 * order-level (reserveDetails) reservation states.
 *
 * Context:
 * - nextReserveStatus: From vehicle DB, can be "RESERVED" or null
 * - order.reserveDetails: From order DB, contains full reservation state
 * - nextOrderReserveStatus: From order DB, indicates the next reservation status(NextOrderReserveStatus)
 */

import { VehicleReserveStatus, VehicleStockStatus } from 'sales-common';
import { type IReserveDetails, type IOrder, IfsAvailabilityCode, NextOrderReserveStatus } from 'next-common';
import type { IDynamicListStockData } from '@common/interfaces/vehicle-stock';
import { ReserveButtonStages } from 'types';

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

/**
 * Unified vehicle reservation data - can come from vehicle or order
 */
export interface IVehicleReservationState {
  nextReserveStatus?: string | null;
  status?: string | null;
  availabilityCode?: string | null;
}

/**
 * Statuses that block cancellation actions
 */
const BLOCKING_STATUSES = [
  VehicleReserveStatus[VehicleReserveStatus.RESERVED],
  VehicleReserveStatus[VehicleReserveStatus.CANCELLATION_FAIL],
  VehicleReserveStatus[VehicleReserveStatus.WAITING],
  VehicleReserveStatus[VehicleReserveStatus.WAITING_CANCELLATION],
] as const;

/**
 * Statuses indicating waiting/pending state
 */
const WAITING_STATUSES = [
  VehicleReserveStatus[VehicleReserveStatus.WAITING],
  VehicleReserveStatus[VehicleReserveStatus.WAITING_CANCELLATION],
] as const;

/**
 * Statuses indicating active reservation
 */
const RESERVED_STATUSES = [
  VehicleReserveStatus[VehicleReserveStatus.RESERVED],
  VehicleReserveStatus[VehicleReserveStatus.CANCELLATION_FAIL],
] as const;

/**
 * Reservation status UI configuration
 */
export const reservationStatus = [
  {
    key: 'WAITING',
    color: 'var(--color-oker20)',
    borderColor: 'var(--color-neutral)',
    label: 'Venter',
  },
  {
    key: 'RESERVED',
    color: 'var(--color-oker20)',
    borderColor: 'var(--color-neutral)',
    label: 'Reservert',
  },
  {
    key: 'RESERVATION_FAIL',
    color: 'var(--color-poppy20)',
    borderColor: 'var(--color-poppy60)',
    label: 'Feilet',
  },
  {
    key: 'WAITING_CANCELLATION',
    color: 'var(--color-oker20)',
    borderColor: 'var(--color-neutral)',
    label: 'Venter avbest',
  },
  {
    key: 'CANCELLATION_FAIL',
    color: 'var(--color-poppy20)',
    borderColor: 'var(--color-poppy60)',
    label: 'Feilet',
  },
  {
    key: 'RESERVATION_CANCELED',
    color: 'var(--color-poppy20)',
    borderColor: 'var(--color-poppy60)',
    label: 'Kansellert',
  },
] as const;

// ============================================================================
// CORE RESERVATION STATUS CHECKS
// ============================================================================

/**
 * Unified check if a vehicle is reserved
 * Handles both vehicle-level (nextReserveStatus/status) and order-level checks
 *
 * @param vehicle - Vehicle data with potential reservation status
 * @returns true if vehicle is currently reserved
 */
export const isVehicleReserved = (
  vehicle: IDynamicListStockData | IVehicleReservationState | undefined | null //IVehicleStock
): boolean => {
  if (!vehicle) return false;

  // Check nextReserveStatus (from vehicle DB)
  if (vehicle.nextReserveStatus?.toString() === VehicleStockStatus.RESERVED) {
    return true;
  }

  // Check status field (can be on vehicle or order)
  if (vehicle.status === VehicleStockStatus.RESERVED) {
    return true;
  }

  // Check IFS availability code
  if ('availabilityCode' in vehicle && vehicle.availabilityCode?.includes(IfsAvailabilityCode.Reserved)) {
    return true;
  }

  return false;
};

/**
 * Check if vehicle is reserved from an external source (e.g. IFS)
 * @param vehicle - Vehicle data which may indicate external reservation status
 * @returns - true if vehicle is reserved from an external source, false otherwise
 */
export const isVehicleReservedExternally = (
  vehicle: IDynamicListStockData | IVehicleReservationState | undefined | null
): boolean => {
  if (!vehicle) return false;

  // If vehicle doesn't have nextReserveStatus but is marked as RESERVED, it's likely reserved from an external source (e.g. IFS)
  if (!vehicle.hasOwnProperty('nextReserveStatus') && vehicle.status === VehicleStockStatus.RESERVED) {
    return true;
  }
  return false;
};

/**
 * Check if reservation status blocks cancellation
 * Used to disable cancel/delete actions when reservation is in certain states
 *
 * @param details - Order reservation details
 * @returns true if reservation blocks cancellation
 */
export const hasReservationBlockingCancellation = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return false;
  return BLOCKING_STATUSES.includes(details.vehicleReserveStatus.toString() as any);
};

/**
 * Check if reservation is in waiting/pending state
 * Used for polling and UI feedback during async operations
 *
 * @param details - Order reservation details
 * @returns true if reservation is waiting for confirmation
 */
export const hasReservationWaitingState = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return false;
  return WAITING_STATUSES.includes(details.vehicleReserveStatus.toString() as any);
};

/**
 * Check if car is actively reserved with quotation
 * Used for business logic requiring confirmed reservation
 *
 * @param details - Order reservation details
 * @returns true if car is reserved with quotation number
 */
export const carIsReservedState = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return false;

  // Must have quotation number to be considered actively reserved
  if (!details.quotationNumber) return false;

  return RESERVED_STATUSES.includes(details.vehicleReserveStatus.toString() as any);
};

// ============================================================================
// RESERVATION DATE & EXPIRATION
// ============================================================================

/**
 * Check if reservation has expired based on end date
 *
 * @param reservationToDate - ISO date string of reservation expiry
 * @returns true if reservation date is in the past
 */
export const isReservationExpired = (reservationToDate: string | null | undefined): boolean => {
  if (!reservationToDate) return false;

  const currentDate = new Date();
  const reservationDate = new Date(reservationToDate);

  return reservationDate < currentDate;
};

/**
 * Check if reservation is canceled
 *
 * @param details - Order reservation details
 * @returns true if reservation was canceled
 */
export const isReservationCanceled = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return false;
  return details.vehicleReserveStatus?.toString() === VehicleReserveStatus[VehicleReserveStatus.RESERVATION_CANCELED];
};

/**
 * Check if reservation is canceled
 *
 * @param details - Order reservation details
 * @returns true if reservation was canceled
 */
export const isReservationFailed = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return false;
  return details.vehicleReserveStatus?.toString() === VehicleReserveStatus[VehicleReserveStatus.RESERVATION_FAIL];
};

// ============================================================================
// BUTTON STAGE CALCULATION
// ============================================================================

/**
 * Calculate the appropriate reserve button stage from order state
 * Determines UI state for reservation buttons
 *
 * @param order - Current order with reservation details
 * @returns Appropriate button stage enum value
 */
export const calculateReserveButtonStage = (order: IOrder | undefined | null): ReserveButtonStages => {
  if (!order) return ReserveButtonStages.NO_RESERVE_REQUEST;

  const reserveDetails = order.reserveDetails;

  // Check if waiting (including if customer not yet created)
  const isWaiting = hasReservationWaitingState(reserveDetails) || !order.customer?.reference?.bosApiId;
  if (isWaiting) {
    return ReserveButtonStages.WAITING;
  }

  // Check if reserved/cancellation-failed
  const isReserved =
    reserveDetails?.vehicleReserveStatus?.toString() === VehicleReserveStatus[VehicleReserveStatus.CANCELLATION_FAIL] ||
    carIsReservedState(reserveDetails) ||
    !!reserveDetails?.quotationNumber;

  if (isReserved) {
    return ReserveButtonStages.RESERVED;
  }

  return ReserveButtonStages.NO_RESERVE_REQUEST;
};

// ============================================================================
// POLLING & ASYNC STATE
// ============================================================================

/**
 * Check if an IFS reservation is currently in progress based on order details
 * This includes both waiting for initial reservation and waiting for cancellation after a cancel action
 * @param order - Current order with reservation details
 * @returns true if IFS reservation is in progress
 */
export const isIFSReservationInProgress = (order: IOrder | undefined | null): boolean => {
  const isReservationInWaiting = !order?.reserveDetails?.vehicleReserveStatus && Boolean(order?.nextOrderReserveStatus);
  const isCanceledButPending =
    isReservationCanceled(order?.reserveDetails) && order?.nextOrderReserveStatus === NextOrderReserveStatus.PENDING;
  const isFailedButPending =
    isReservationFailed(order?.reserveDetails) && order?.nextOrderReserveStatus === NextOrderReserveStatus.PENDING;

  return isReservationInWaiting || isCanceledButPending || isFailedButPending;
};

/**
 * Check if reservation is in a terminal/final state
 * Terminal states don't require further polling or updates
 *
 * @param details - Order reservation details
 * @returns true if reservation is in a final state
 */
export const isReservationTerminal = (details: IReserveDetails | undefined | null): boolean => {
  if (!details?.vehicleReserveStatus) return true;

  const status = details.vehicleReserveStatus?.toString();

  // Terminal states
  return (
    status === VehicleReserveStatus[VehicleReserveStatus.RESERVED] ||
    status === VehicleReserveStatus[VehicleReserveStatus.RESERVATION_CANCELED] ||
    status === VehicleReserveStatus[VehicleReserveStatus.RESERVATION_FAIL] ||
    status === VehicleReserveStatus[VehicleReserveStatus.CANCELLATION_FAIL]
  );
};

// ============================================================================
// VALIDATION & PERMISSIONS
// ============================================================================

/**
 * Check if user can initiate a new reservation
 *
 * @param order - Current order state
 * @returns true if reservation action is allowed
 */
export const canReserveVehicle = (order: IOrder | undefined | null): boolean => {
  if (!order) return false;

  const reserveDetails = order.reserveDetails;

  // Can't reserve if already in blocking state
  if (hasReservationBlockingCancellation(reserveDetails)) {
    return false;
  }

  // Can't reserve if waiting for previous operation
  if (hasReservationWaitingState(reserveDetails)) {
    return false;
  }

  return true;
};

/**
 * Check if user can cancel existing reservation
 *
 * @param order - Current order state
 * @returns true if cancellation is allowed
 */
export const canCancelReservation = (order: IOrder | undefined | null): boolean => {
  if (!order) return false;

  const reserveDetails = order.reserveDetails;

  // Must be in reserved state to cancel
  if (!carIsReservedState(reserveDetails)) {
    return false;
  }

  // Can't cancel if already waiting for cancellation
  if (
    reserveDetails?.vehicleReserveStatus?.toString() === VehicleReserveStatus[VehicleReserveStatus.WAITING_CANCELLATION]
  ) {
    return false;
  }

  return true;
};

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Determine if reservation info should be displayed
 *
 * @param details - Order reservation details
 * @param reservationToDate - Reservation end date
 * @returns true if reservation info is relevant to show
 */
export const shouldShowReservationInfo = (
  details: IReserveDetails | undefined | null,
  reservationToDate: string | null | undefined
): boolean => {
  if (!reservationToDate) return false;
  if (!details) return false;

  // Don't show if waiting
  if (hasReservationWaitingState(details)) return false;

  // Don't show if canceled
  if (isReservationCanceled(details)) return false;

  // Don't show if expired
  if (isReservationExpired(reservationToDate)) return false;

  // Don't show if reservation failed
  if (isReservationFailed(details)) return false;

  return true;
};
